<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { gsap } from 'gsap'
import * as Matter from 'matter-js'
import { Howl } from 'howler'
import ToiletFish from './ToiletFish.vue'
import ToiletFood from './ToiletFood.vue'
import PowerMeter from './PowerMeter.vue'
import AchievementNotification from './AchievementNotification.vue'

// 游戏状态
const gameContainer = ref<HTMLDivElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const powerLevel = ref(0)
const isCharging = ref(false)
const isAiming = ref(false)
const foods = ref<Array<any>>([])
const fishes = ref<Array<any>>([])
const achievements = ref<string[]>([])
const showAchievement = ref(false)
const currentAchievement = ref('')
const score = ref(0)
const feedbackMessage = ref('')
const showFeedback = ref(false)
const gameGoal = ref({ current: 0, target: 5, text: '喂养5条鱼' })
const showGoalComplete = ref(false)
const selectedFoodIndex = ref(0)
const showFoodSelector = ref(false)

// 物理引擎相关
let engine: Matter.Engine
let render: Matter.Render
let runner: Matter.Runner
let world: Matter.World

// 音效
const sounds = {
  flush: new Howl({ src: ['/sounds/toilet_flush.mp3'] }),
  splash: new Howl({ src: ['/sounds/splash.mp3'] }),
  fish: new Howl({ src: ['/sounds/fish_bubbles.mp3'] }),
  achievement: new Howl({ src: ['/sounds/achievement.mp3'] }),
}

// 饲料类型
const foodTypes = [
  { name: '便便', image: '/images/poop.png', weight: 0.5, value: 10, unlocked: true },
  { name: '榴莲', image: '/images/durian.png', weight: 1, value: 20, unlocked: true },
  { name: '臭袜子', image: '/images/sock.png', weight: 0.3, value: 15, unlocked: true },
  { name: '电视机', image: '/images/tv.png', weight: 5, value: 50, unlocked: false },
  { name: '诺基亚', image: '/images/nokia.png', weight: 2, value: 30, unlocked: false },
]

// 鱼类种类
const fishTypes = [
  { name: '三眼马桶鱼', image: '/images/toilet_fish.png', speed: 1, value: 100 },
  { name: '多头蠕虫鱼', image: '/images/worm_fish.png', speed: 0.8, value: 150 },
  { name: '像素幽灵鱼', image: '/images/ghost_fish.png', speed: 1.2, value: 200 },
]

// 计算蓄力效果
const powerStyle = computed(() => {
  return {
    height: `${powerLevel.value}%`,
    backgroundColor:
      powerLevel.value < 30 ? '#5eff7a' : powerLevel.value < 70 ? '#fff95e' : '#ff5e5e',
  }
})

// 初始化物理引擎
const initPhysics = () => {
  if (!canvasRef.value || !gameContainer.value) return

  const containerWidth = gameContainer.value.clientWidth
  const containerHeight = gameContainer.value.clientHeight

  // 创建引擎
  engine = Matter.Engine.create({
    gravity: { x: 0, y: 0.5 },
  })
  world = engine.world

  // 创建渲染器
  render = Matter.Render.create({
    canvas: canvasRef.value,
    engine: engine,
    options: {
      width: containerWidth,
      height: containerHeight,
      wireframes: false,
      background: 'transparent',
    },
  })

  // 创建底部墙壁
  const floor = Matter.Bodies.rectangle(
    containerWidth / 2,
    containerHeight + 50,
    containerWidth,
    100,
    { isStatic: true },
  )

  // 创建左右墙壁
  const leftWall = Matter.Bodies.rectangle(-50, containerHeight / 2, 100, containerHeight, {
    isStatic: true,
  })

  const rightWall = Matter.Bodies.rectangle(
    containerWidth + 50,
    containerHeight / 2,
    100,
    containerHeight,
    { isStatic: true },
  )

  // 将墙壁添加到世界
  Matter.Composite.add(world, [floor, leftWall, rightWall])

  // 开始运行
  runner = Matter.Runner.create()
  Matter.Runner.run(runner, engine)
  Matter.Render.run(render)

  // 添加初始鱼群
  spawnInitialFish()
}

// 生成初始鱼群
const spawnInitialFish = () => {
  if (!gameContainer.value) return

  const containerWidth = gameContainer.value.clientWidth
  const containerHeight = gameContainer.value.clientHeight

  for (let i = 0; i < 6; i++) {
    const fishType = fishTypes[Math.floor(Math.random() * fishTypes.length)]
    const fish = {
      id: Date.now() + i,
      type: fishType,
      x: Math.random() * containerWidth,
      y: containerHeight * 0.6 + Math.random() * (containerHeight * 0.3),
      direction: Math.random() > 0.5 ? 1 : -1,
      scale: 0.8 + Math.random() * 0.4,
      hungry: Math.random() > 0.5,
    }

    fishes.value.push(fish)
  }
}

// 蓄力过程
const startCharging = () => {
  if (isCharging.value) return

  isCharging.value = true
  const chargingInterval = setInterval(() => {
    if (powerLevel.value < 100) {
      powerLevel.value += 2
    } else {
      clearInterval(chargingInterval)
    }
  }, 50)

  // 自动停止蓄力
  setTimeout(() => {
    if (isCharging.value) {
      clearInterval(chargingInterval)
      releaseFood()
    }
  }, 5000)
}

// 释放食物
const releaseFood = () => {
  if (!isCharging.value || !gameContainer.value) return

  isCharging.value = false
  isAiming.value = true

  // 播放冲水声
  sounds.flush.play()

  const power = powerLevel.value / 100
  const containerWidth = gameContainer.value.clientWidth

  // 使用已选择的食物类型
  const foodType = foodTypes[selectedFoodIndex.value]

  // 发射食物
  const newFood = {
    id: Date.now(),
    type: foodType,
    x: containerWidth / 2,
    y: 100,
    velocityX: (Math.random() * 2 - 1) * 3 * power,
    velocityY: -10 + 15 * power,
    rotation: 0,
    scale: 0.8 + Math.random() * 0.4,
  }

  foods.value.push(newFood)

  // 显示反馈信息
  showFeedbackMessage(`投喂了${foodType.name}！`)

  // 重置蓄力
  powerLevel.value = 0

  // 延迟后恢复瞄准状态
  setTimeout(() => {
    isAiming.value = false
    sounds.splash.play()

    // 检查成就
    checkAchievements()
  }, 1000)
}

// 切换食物选择器显示
const toggleFoodSelector = () => {
  showFoodSelector.value = !showFoodSelector.value
}

// 选择食物
const selectFood = (index: number) => {
  if (foodTypes[index].unlocked) {
    selectedFoodIndex.value = index
    showFoodSelector.value = false
  } else {
    // 显示未解锁提示
    showFeedbackMessage(`${foodTypes[index].name}尚未解锁！`)
  }
}

// 显示反馈信息
const showFeedbackMessage = (message: string) => {
  feedbackMessage.value = message
  showFeedback.value = true

  setTimeout(() => {
    showFeedback.value = false
  }, 2000)
}

// 检查成就
const checkAchievements = () => {
  if (foods.value.length >= 5 && !achievements.value.includes('饲料大师')) {
    unlockAchievement('饲料大师')
  }

  if (score.value >= 500 && !achievements.value.includes('深渊凝视者')) {
    unlockAchievement('深渊凝视者')
  }

  // 检查是否投喂了所有类型的食物
  const fedTypes = new Set(foods.value.map((food) => food.type.name))
  if (fedTypes.size >= foodTypes.length && !achievements.value.includes('味觉崩坏')) {
    unlockAchievement('味觉崩坏')
  }

  // 更新成就目标
  if (gameGoal.value.text === '解锁3个成就') {
    gameGoal.value.current = achievements.value.length

    if (gameGoal.value.current >= 3) {
      // 完成成就目标
      showGoalComplete.value = true
      setTimeout(() => {
        showGoalComplete.value = false
        // 设置新目标
        gameGoal.value = { current: fishes.value.length, target: 10, text: '培育10条鱼' }
      }, 3000)
    }
  }
}

// 解锁成就
const unlockAchievement = (achievement: string) => {
  achievements.value.push(achievement)
  currentAchievement.value = achievement
  showAchievement.value = true
  sounds.achievement.play()

  setTimeout(() => {
    showAchievement.value = false
  }, 3000)
}

// 更新鱼群行为
const updateFishes = () => {
  if (!gameContainer.value) return

  const containerWidth = gameContainer.value.clientWidth
  const containerHeight = gameContainer.value.clientHeight

  fishes.value.forEach((fish) => {
    // 随机移动
    fish.x += fish.type.speed * fish.direction * (fish.hungry ? 0.5 : 1) * 2

    // 转向
    if (fish.x < 0 || fish.x > containerWidth) {
      fish.direction *= -1
    }

    // 寻找附近的食物
    const nearbyFood = foods.value.find((food) => {
      const distance = Math.sqrt(Math.pow(fish.x - food.x, 2) + Math.pow(fish.y - food.y, 2))
      return distance < 100 && fish.hungry
    })

    // 如果找到食物，追逐它
    if (nearbyFood) {
      const dx = nearbyFood.x - fish.x
      const dy = nearbyFood.y - fish.y
      const angle = Math.atan2(dy, dx)

      fish.x += Math.cos(angle) * fish.type.speed * 3
      fish.y += Math.sin(angle) * fish.type.speed * 3

      // 如果足够近，吃掉食物
      const eatDistance = Math.sqrt(
        Math.pow(fish.x - nearbyFood.x, 2) + Math.pow(fish.y - nearbyFood.y, 2),
      )

      if (eatDistance < 30) {
        // 移除食物
        foods.value = foods.value.filter((f) => f.id !== nearbyFood.id)

        // 鱼不再饥饿
        fish.hungry = false

        // 播放进食音效
        sounds.fish.play()

        // 增加分数
        const pointsGained = nearbyFood.type.value + fish.type.value
        score.value += pointsGained

        // 显示得分反馈
        showFeedbackMessage(`+${pointsGained} 分！`)

        // 更新游戏目标
        updateGameGoal('feed')

        // 有概率产生新鱼
        if (Math.random() < 0.3) {
          const newFishType = fishTypes[Math.floor(Math.random() * fishTypes.length)]
          const newFish = {
            id: Date.now(),
            type: newFishType,
            x: fish.x,
            y: fish.y,
            direction: Math.random() > 0.5 ? 1 : -1,
            scale: 0.6 + Math.random() * 0.3,
            hungry: true,
          }

          fishes.value.push(newFish)

          // 显示新鱼反馈
          showFeedbackMessage(`出现了${newFishType.name}！`)
        }

        // 一段时间后再次饥饿
        setTimeout(
          () => {
            if (fish && fishes.value.some((f) => f.id === fish.id)) {
              fish.hungry = true
            }
          },
          5000 + Math.random() * 5000,
        )
      }
    }

    // 随机深度变化
    if (Math.random() < 0.01) {
      fish.y += (Math.random() - 0.5) * 30

      // 限制鱼的深度
      fish.y = Math.max(containerHeight * 0.4, Math.min(containerHeight - 50, fish.y))
    }
  })
}

// 游戏更新循环
let animationFrame: number
const gameLoop = () => {
  updateFishes()
  animationFrame = requestAnimationFrame(gameLoop)
}

// 重置大小
const handleResize = () => {
  if (!canvasRef.value || !gameContainer.value || !render) return

  const containerWidth = gameContainer.value.clientWidth
  const containerHeight = gameContainer.value.clientHeight

  render.options.width = containerWidth
  render.options.height = containerHeight
  render.canvas.width = containerWidth
  render.canvas.height = containerHeight
}

// 添加游戏目标管理
const updateGameGoal = (action: string) => {
  if (action === 'feed') {
    if (gameGoal.value.text === '喂养5条鱼') {
      gameGoal.value.current++

      if (gameGoal.value.current >= 5) {
        // 完成第一个目标，解锁新饲料
        foodTypes[3].unlocked = true // 解锁电视机
        showFeedbackMessage('解锁了新饲料：电视机！')

        showGoalComplete.value = true
        setTimeout(() => {
          showGoalComplete.value = false
          // 设置新目标
          gameGoal.value = { current: score.value, target: 500, text: '获得500点分数' }
        }, 3000)
      }
    } else if (gameGoal.value.text === '获得500点分数') {
      gameGoal.value.current = score.value

      if (gameGoal.value.current >= 500) {
        // 完成第二个目标，解锁最后一种饲料
        foodTypes[4].unlocked = true // 解锁诺基亚
        showFeedbackMessage('解锁了新饲料：诺基亚！')

        // 完成第二个目标
        showGoalComplete.value = true
        setTimeout(() => {
          showGoalComplete.value = false
          // 设置新目标
          gameGoal.value = { current: achievements.value.length, target: 3, text: '解锁3个成就' }
        }, 3000)
      }
    } else if (gameGoal.value.text === '解锁3个成就') {
      gameGoal.value.current = achievements.value.length

      if (gameGoal.value.current >= 3) {
        // 完成所有目标
        showGoalComplete.value = true
        setTimeout(() => {
          showGoalComplete.value = false
          // 设置终极目标
          gameGoal.value = { current: fishes.value.length, target: 10, text: '培育10条鱼' }
        }, 3000)
      }
    } else if (gameGoal.value.text === '培育10条鱼') {
      gameGoal.value.current = fishes.value.length
    }
  }
}

// 生命周期钩子
onMounted(() => {
  initPhysics()
  gameLoop()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
  }

  if (runner) {
    Matter.Runner.stop(runner)
  }

  if (render) {
    Matter.Render.stop(render)
  }

  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div ref="gameContainer" class="game-container">
    <canvas ref="canvasRef" class="physics-canvas"></canvas>

    <!-- 水面 -->
    <div class="water-surface">
      <div class="water-wave"></div>
    </div>

    <!-- 马桶 -->
    <div class="toilet" :class="{ 'toilet-charging': isCharging, 'toilet-releasing': isAiming }">
      <div class="toilet-seat"></div>
      <div class="toilet-bowl"></div>
    </div>

    <!-- 蓄力条 -->
    <PowerMeter
      :power-level="powerLevel"
      :is-charging="isCharging"
      @start-charge="startCharging"
      @release="releaseFood"
    />

    <!-- 食物选择器按钮 -->
    <div class="food-selector-toggle" @click="toggleFoodSelector">
      <img :src="foodTypes[selectedFoodIndex].image" alt="选择饲料" />
      <div class="selector-arrow">▼</div>
    </div>

    <!-- 食物选择面板 -->
    <div class="food-selector-panel" v-if="showFoodSelector">
      <div
        v-for="(food, index) in foodTypes"
        :key="index"
        class="food-option"
        :class="{ selected: index === selectedFoodIndex, locked: !food.unlocked }"
        @click="selectFood(index)"
      >
        <img :src="food.image" :alt="food.name" />
        <div class="food-info">
          <div class="food-name">{{ food.name }}</div>
          <div class="food-value">价值: {{ food.value }}点</div>
          <div class="food-weight">重量: {{ food.weight }}</div>
        </div>
        <div class="lock-icon" v-if="!food.unlocked">🔒</div>
      </div>
    </div>

    <!-- 分数 -->
    <div class="score-display">
      <span>深渊点数: {{ score }}</span>
    </div>

    <!-- 游戏目标 -->
    <div class="goal-display">
      <div class="goal-title">目标</div>
      <div class="goal-text">{{ gameGoal.text }}</div>
      <div class="goal-progress">
        <div
          class="goal-progress-bar"
          :style="{ width: `${Math.min(100, (gameGoal.current / gameGoal.target) * 100)}%` }"
        ></div>
        <span class="goal-progress-text">{{ gameGoal.current }}/{{ gameGoal.target }}</span>
      </div>
    </div>

    <!-- 目标完成提示 -->
    <div class="goal-complete-banner" v-if="showGoalComplete">
      <div class="goal-complete-icon">🎯</div>
      <div class="goal-complete-text">目标完成！</div>
    </div>

    <!-- 反馈信息 -->
    <div class="feedback-message" v-if="showFeedback">
      {{ feedbackMessage }}
    </div>

    <!-- 食物渲染 -->
    <ToiletFood v-for="food in foods" :key="food.id" :food="food" />

    <!-- 鱼群渲染 -->
    <ToiletFish v-for="fish in fishes" :key="fish.id" :fish="fish" />

    <!-- 成就通知 -->
    <AchievementNotification v-if="showAchievement" :achievement="currentAchievement" />
  </div>
</template>

<style scoped>
.game-container {
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(to bottom, #000430, #000028);
}

.physics-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
}

.water-surface {
  position: absolute;
  top: 35%;
  left: 0;
  width: 100%;
  height: 65%;
  background: linear-gradient(to bottom, rgba(7, 11, 52, 0.8), rgba(2, 3, 20, 0.95));
  z-index: 1;
  overflow: hidden;
}

.water-wave {
  position: absolute;
  top: -20px;
  left: 0;
  width: 200%;
  height: 40px;
  background: url('/images/wave.png') repeat-x;
  animation: waveAnimation 10s linear infinite;
  opacity: 0.6;
}

.toilet {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  width: 120px;
  height: 120px;
  transition: transform 0.3s ease;
}

.toilet-charging {
  animation: toiletShaking 0.1s ease-in-out infinite;
}

.toilet-releasing {
  animation: toiletFlush 1s ease;
}

.toilet-seat {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 20px;
  background: white;
  border-radius: 50% 50% 0 0;
  transform-origin: bottom;
  transition: transform 0.3s ease;
}

.toilet-bowl {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 100px;
  background: white;
  border-radius: 10px 10px 30px 30px;
  box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.2);
}

.score-display {
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 10px 15px;
  background: rgba(0, 0, 0, 0.6);
  color: #4fc2ff;
  border-radius: 10px;
  font-size: 18px;
  font-weight: bold;
  z-index: 10;
}

.goal-display {
  position: absolute;
  top: 20px;
  left: 20px;
  padding: 10px 15px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border-radius: 10px;
  font-size: 14px;
  z-index: 10;
  min-width: 180px;
}

.goal-title {
  color: #ffd700;
  font-weight: bold;
  margin-bottom: 5px;
}

.goal-text {
  margin-bottom: 8px;
}

.goal-progress {
  height: 10px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  overflow: hidden;
  position: relative;
}

.goal-progress-bar {
  height: 100%;
  background: linear-gradient(to right, #4fc2ff, #01f7a7);
  transition: width 0.5s ease;
}

.goal-progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 10px;
  color: white;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.8);
}

.feedback-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px 20px;
  border-radius: 30px;
  font-size: 20px;
  font-weight: bold;
  z-index: 100;
  white-space: nowrap;
  animation: fadeInOut 2s ease;
}

.goal-complete-banner {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  border: 2px solid #ffd700;
  border-radius: 15px;
  padding: 15px 25px;
  z-index: 100;
  animation: goalCompleteAnim 3s ease;
}

.goal-complete-icon {
  font-size: 30px;
  margin-right: 15px;
}

.goal-complete-text {
  font-size: 24px;
  font-weight: bold;
  color: #ffd700;
}

@keyframes waveAnimation {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

@keyframes toiletShaking {
  0% {
    transform: translateX(-50%) rotate(-1deg);
  }
  50% {
    transform: translateX(-50%) rotate(1deg);
  }
  100% {
    transform: translateX(-50%) rotate(-1deg);
  }
}

@keyframes toiletFlush {
  0% {
    transform: translateX(-50%) scale(1);
  }
  20% {
    transform: translateX(-50%) scale(1.1);
  }
  40% {
    transform: translateX(-50%) scale(0.95);
  }
  60% {
    transform: translateX(-50%) scale(1.05);
  }
  80% {
    transform: translateX(-50%) scale(0.98);
  }
  100% {
    transform: translateX(-50%) scale(1);
  }
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  20% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  80% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
}

@keyframes goalCompleteAnim {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  10% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.1);
  }
  20% {
    transform: translate(-50%, -50%) scale(1);
  }
  80% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
}

.food-selector-toggle {
  position: absolute;
  bottom: 40px;
  right: 40px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  border: 2px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  overflow: hidden;
}

.food-selector-toggle:hover {
  transform: scale(1.1);
  border-color: rgba(255, 255, 255, 0.8);
}

.food-selector-toggle img {
  width: 70%;
  height: 70%;
  object-fit: contain;
}

.selector-arrow {
  position: absolute;
  bottom: 2px;
  right: 2px;
  font-size: 12px;
  color: white;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.food-selector-panel {
  position: absolute;
  bottom: 110px;
  right: 40px;
  width: 280px;
  background: rgba(0, 0, 0, 0.85);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px;
  z-index: 15;
  animation: fadeIn 0.3s ease;
}

.food-option {
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.food-option:hover:not(.locked) {
  background: rgba(255, 255, 255, 0.1);
}

.food-option.selected {
  background: rgba(79, 194, 255, 0.3);
  border: 1px solid #4fc2ff;
}

.food-option.locked {
  opacity: 0.6;
  filter: grayscale(0.8);
  cursor: not-allowed;
}

.food-option img {
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-right: 15px;
}

.food-info {
  color: white;
}

.food-name {
  font-weight: bold;
  margin-bottom: 3px;
}

.food-value {
  font-size: 12px;
  color: #ffd700;
}

.food-weight {
  font-size: 12px;
  color: #aaa;
}

.lock-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
